import DefaultImage from '../assets/img/default_img.png';
import { StrapiImage } from '../core/models/strapi-image.model';
import MarketplaceBackground from '../assets/img/marketplaceBackground.jpeg';
import { StrapiAddress } from '../core/models/strapi-address.model';
import { StrapiRegionImage } from '../core/models/strapi-region-image.model';
import { meilisearchClient } from '../core/search/meilisearch';
import { MeiliIndexes } from '../constants/MeiliIndexes';
import { Logger } from '../core/logger/Logger';

const getPreviewImageUrl = (item?: StrapiImage, actualSize = false): string => {
  if (item) {
    if (actualSize && item.formats && item.formats.large) {
      return `${process.env.REACT_APP_STRAPI_ENDPOINT}${item.formats.large.url}`;
    }
    if (item.formats && item.formats.medium) {
      return `${process.env.REACT_APP_STRAPI_ENDPOINT}${item.formats.medium.url}`;
    }
    if (item.formats && item.formats.small) {
      return `${process.env.REACT_APP_STRAPI_ENDPOINT}${item.formats.small.url}`;
    }
    if (item.formats && item.formats.thumbnail) {
      return `${process.env.REACT_APP_STRAPI_ENDPOINT}${item.formats.thumbnail.url}`;
    }
    if (item?.fileWithMeta?.file) {
      return createImagePreviewFromUploadFile(item).src;
    }
    // Strapi only generates a format when the original is larger than that
    // breakpoint, so an image narrower than 500px legitimately has none at
    // all. Falling back to the original beats rendering the placeholder.
    if (item?.url) {
      return `${process.env.REACT_APP_STRAPI_ENDPOINT}${item.url}`;
    }
  }
  return '';
};

const getVendorHeaderImage = (item: any): string => {
  if (item.mainImage) {
    return getPreviewImageUrl(item.mainImage, true);
  }
  if (item.images && item.images.length > 0) {
    const firstImage = item.images[0];
    // Prefer a generated format over the original upload — the original is
    // full-resolution and can be several megabytes.
    if (firstImage?.formats?.small) {
      return `${process.env.REACT_APP_STRAPI_ENDPOINT}${firstImage.formats.small.url}`;
    }
    if (firstImage && firstImage.url) {
      return `${process.env.REACT_APP_STRAPI_ENDPOINT}${firstImage.url}`;
    }
  }
  return DefaultImage;
};

const getPreviewImage = (item: any, actualSize = false): string => {
  if (item?.mainImage) {
    if (actualSize && item.mainImage.url) {
      return `${process.env.REACT_APP_STRAPI_ENDPOINT}${item.mainImage.url}`;
    }
    if (item.mainImage.formats && item.mainImage.formats.large) {
      return `${process.env.REACT_APP_STRAPI_ENDPOINT}${item.mainImage.formats.large.url}`;
    }
    if (item.mainImage.formats && item.mainImage.formats.medium) {
      return `${process.env.REACT_APP_STRAPI_ENDPOINT}${item.mainImage.formats.medium.url}`;
    }
    if (item.mainImage.formats && item.mainImage.formats.small) {
      return `${process.env.REACT_APP_STRAPI_ENDPOINT}${item.mainImage.formats.small.url}`;
    }

    if (item.mainImage.formats && item.mainImage.formats.thumbnail) {
      return `${process.env.REACT_APP_STRAPI_ENDPOINT}${item.mainImage.formats.thumbnail.url}`;
    }
    // mainImage is the explicit choice, so serve the original rather than
    // falling through to logo/images when it has no generated format.
    if (item.mainImage.url) {
      return `${process.env.REACT_APP_STRAPI_ENDPOINT}${item.mainImage.url}`;
    }
  }

  if (item?.logo) {
    if (actualSize && item.logo.url) {
      return `${process.env.REACT_APP_STRAPI_ENDPOINT}${item.logo.url}`;
    }
    if (item.logo.formats && item.logo.formats.small) {
      return `${process.env.REACT_APP_STRAPI_ENDPOINT}${item.logo.formats.small.url}`;
    }
    if (item.logo.url) {
      return `${process.env.REACT_APP_STRAPI_ENDPOINT}${item.logo.url}`;
    }
  }

  if (item?.images) {
    return getVendorHeaderImage(item);
  }

  return DefaultImage;
};

const getAllImages = (item: any): Array<StrapiImage> => {
  const images = [];

  if (item.mainImage) {
    images.push(item.mainImage);
  }

  if (item.images) {
    images.push(...item.images);
  }

  return images;
};

const getLogo = (item: any): string => {
  if (item.logo) {
    if (item.logo.formats && item.logo.formats.small) {
      return `${process.env.REACT_APP_STRAPI_ENDPOINT}${item.logo.formats.small.url}`;
    }
    if (item.logo.formats && item.logo.formats.thumbnail) {
      return `${process.env.REACT_APP_STRAPI_ENDPOINT}${item.logo.formats.thumbnail.url}`;
    }
    // A logo is usually well under the 500px `small` breakpoint, so it often
    // has no generated format at all — without this it always fell through.
    if (item.logo.url) {
      return `${process.env.REACT_APP_STRAPI_ENDPOINT}${item.logo.url}`;
    }
  }

  return DefaultImage;
};

const getFirstImage = (images: Array<StrapiImage>, actualSize = false): string | null => {
  if (images && images.length > 0) {
    const firstImage = images[0];

    if (actualSize && firstImage.url) {
      return `${process.env.REACT_APP_STRAPI_ENDPOINT}${firstImage.url}`;
    }
    if (firstImage.formats && firstImage.formats.medium) {
      return `${process.env.REACT_APP_STRAPI_ENDPOINT}${firstImage.formats.medium.url}`;
    }
    if (firstImage.formats && firstImage.formats.small) {
      return `${process.env.REACT_APP_STRAPI_ENDPOINT}${firstImage.formats.small.url}`;
    }
    if (firstImage.formats && firstImage.formats.thumbnail) {
      return `${process.env.REACT_APP_STRAPI_ENDPOINT}${firstImage.formats.thumbnail.url}`;
    }
    if (firstImage.url) {
      return `${process.env.REACT_APP_STRAPI_ENDPOINT}${firstImage.url}`;
    }
  }
  return null;
};

const getHeaderImage = (images: Array<StrapiImage>): string => {
  if (images && images.length > 0) {
    const firstImage = images[0];

    // Prefer a generated format over the original upload — the original is
    // full-resolution and is the largest asset on the page when it wins.
    if (firstImage?.formats?.large) {
      return `${process.env.REACT_APP_STRAPI_ENDPOINT}${firstImage.formats.large.url}`;
    }
    if (firstImage?.formats?.medium) {
      return `${process.env.REACT_APP_STRAPI_ENDPOINT}${firstImage.formats.medium.url}`;
    }
    if (firstImage.formats && firstImage.formats.small) {
      return `${process.env.REACT_APP_STRAPI_ENDPOINT}${firstImage.formats.small.url}`;
    }

    return getPreviewImageUrl(firstImage);
  }

  return MarketplaceBackground;
};

const createImageUploadFormData = (
  formData: FormData,
  uploadImages: Array<StrapiImage>,
  bindEntity?: {
    entity: string;
    refId: string;
    field: string;
  }
) => {
  uploadImages.forEach((uploadImageItem) => {
    formData.append(`files`, uploadImageItem.fileWithMeta.file, uploadImageItem.name);

    if (bindEntity) {
      formData.append(`ref`, `api::${bindEntity.entity}.${bindEntity.entity}`);
      formData.append(`refId`, bindEntity.refId);
      formData.append(`files.field`, `${uploadImageItem.isMainImage ? 'logo' : 'images'}`);
    } else {
      formData.append(`files.field`, `${uploadImageItem.isMainImage ? 'mainImage' : 'images'}`);
    }
  });

  return formData;
};

export const createImagePreviewFromUploadFile = (strapiImage?: StrapiImage) => {
  if (strapiImage && strapiImage.fileWithMeta) {
    const objectUrl = URL.createObjectURL(strapiImage.fileWithMeta.file);
    const img = new Image();
    img.src = objectUrl;
    return img;
  }

  return {
    src: ''
  };
};

const fetchRegionImage = async (
  address: Pick<StrapiAddress, 'postalCode' | 'city' | 'country'>
): Promise<StrapiRegionImage | null> => {
  const { postalCode, city, country } = address;
  const filters = [];

  if (postalCode?.trim()) {
    filters.push(`address.postalCode = ${postalCode}`);
  }
  if (city?.trim()) {
    filters.push(`address.city = '${city}'`);
  }
  if (country?.trim()) {
    filters.push(`address.country = '${country}'`);
  }
  Logger.debug('filters ', filters);
  for (const filter of filters) {
    try {
      const result = await meilisearchClient
        .index(MeiliIndexes.regionImage)
        .search<StrapiRegionImage>('', {
          filter: [filter]
        });

      if (result.hits.length > 0) {
        return result.hits[0];
      }
    } catch (error) {
      Logger.error(error);
    }
  }

  return null;
};

const fetchRegionImageUrl = async (
  address: Pick<StrapiAddress, 'postalCode' | 'city' | 'country'>
): Promise<string> => {
  const hit = await fetchRegionImage(address);

  if (hit) {
    const firstImage = getFirstImage(hit.images, true);

    if (firstImage) {
      return firstImage;
    }
  }

  return '';
};

export {
  getVendorHeaderImage,
  getPreviewImage,
  getFirstImage,
  getLogo,
  getAllImages,
  getPreviewImageUrl,
  getHeaderImage,
  createImageUploadFormData,
  fetchRegionImageUrl,
  fetchRegionImage
};
